<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  onMounted,
  reactive,
  watch,
} from 'vue'
import { BrushTeeth } from '@/shared/constants/brushTeeth'
import { useRoute } from 'vue-router'
import { brushReportApi } from '@/shared/api'
import { showErrorMessage } from '@/shared/utils'

const props = defineProps({
  currentColor: String,
  currentName: String,
  firstGroupColors: Array,
  secondGroupColors: Array,
  deletedColor: String,
  isChangeFirm: Boolean,
  isUpdateReport: Boolean,
})

const emit = defineEmits([
  'updateColors',
  'updateTeethData',
  'deleteCurrentColor',
  'resetColors',
  'resetDeletedColor',
  'updateReport',
  'resetChangeFirmStatus',
])

const route = useRoute()
const isChangeFirm = computed(() => props.isChangeFirm)
const isNewReport = computed(() => route.params.reportId === '0')
const isPreviewMode = computed(() => route.query['view-mode'] === '1')
const isUpdateReport = computed(() => props.isUpdateReport)

const reportId = computed(() => Number(route.params.reportId))
const patientId = computed(() => Number(route.params.patientId))

const teethData = reactive([])
const teethImages = computed(() => BrushTeeth)
const deletedColor = computed(() => props.deletedColor)
const defaultColor = '#b3b3b3'

for (let quarter = 1; quarter < 5; quarter++) {
  for (let number = 1; number < 9; number++) {
    const tooth = {
      id: Number(`${quarter}${number}`),
      statusTooth: 'Normal',
      arrowColor: ['#b3b3b3', '#b3b3b3'],
      baseColor: '#b3b3b3',
      arrowStatus: 0,
      numberClass: `number${quarter}${number}`,
      class: `tooth${quarter}${number}`,
      img: teethImages.value.find(
        (item) => item.id === Number(`${quarter}${number}`)
      ),
    }

    if (tooth.id !== 41 && tooth.id !== 11) {
      tooth.arrowClass = `arrow${quarter}${number}`
    }

    teethData.push(tooth)
  }
}

const qrCode = computed(() => {
  if (!isPreviewMode.value) {
    return
  }

  let domain = window.origin

  if (process.env.NODE_ENV === 'production') {
    domain = process.env.VUE_APP_BASE_URI
  }

  return `${domain}/api/web/admin/patients/${patientId.value}/get-qr`
})

const getFullReport = async () => {
  const [error, response] = await brushReportApi.getReport(reportId.value)

  if (error) {
    showErrorMessage(error)
  } else {
    if (teethData.length === 32) {
      teethData.length = 0
    }

    response.data.teeth.forEach((tooth) => {
      tooth.img = teethImages.value.find((image) => image.id === tooth.id)
      teethData.push(tooth)
    })
  }
}

const updateStatusTooth = function (tooth) {
  const currentStatusTooth = tooth.statusTooth

  const newStatusTooth = function (currentStatusTooth) {
    if (currentStatusTooth === 'Normal') {
      return 'Implant'
    } else if (currentStatusTooth === 'Implant') {
      return 'Empty'
    }
    return 'Normal'
  }

  tooth.statusTooth = newStatusTooth(currentStatusTooth)
}

const updateArrayColor = function (tooth) {
  if (props.currentColor === defaultColor) {
    return
  }

  const currentColor = props.currentColor

  const firstArrayIncludesColor = props.firstGroupColors.find((item) =>
    item.color.includes(currentColor)
  )
  const secondArrayIncludesColor = props.secondGroupColors.find((item) =>
    item.color.includes(currentColor)
  )

  const updateStatusArrow = function (currentStatusArrow) {
    if (currentStatusArrow < 3) {
      ++tooth.arrowStatus
    } else {
      tooth.arrowStatus = 0
    }
  }
  const includesCurrentColor = function (currentColor) {
    if (!firstArrayIncludesColor && !secondArrayIncludesColor) {
      return
    }

    const matchTeeth = teethData.filter(
      (tooth) =>
        // eslint-disable-next-line no-prototype-builtins
        tooth.hasOwnProperty('arrowClass') &&
        (tooth.arrowColor[0] === currentColor ||
          tooth.arrowColor[1] === currentColor)
    )
    if (matchTeeth.length === 0) {
      emit('deleteCurrentColor', currentColor)
    }
  }

  const checkMatchColor = function (color) {
    if (color === currentColor) {
      return
    }

    addCurrentColor(currentColor)
    tooth.baseColor = currentColor
    tooth.arrowColor[1] = currentColor
    tooth.arrowColor[0] = defaultColor
    tooth.arrowStatus = 1

    if (teethData.filter((tooth) => tooth.baseColor === color).length === 0) {
      emit('deleteCurrentColor', color)
    }
  }

  const addCurrentColor = function (color) {
    if (!firstArrayIncludesColor && !secondArrayIncludesColor) {
      const colorName = props.currentName
      emit('updateColors', { color, colorName })
    }
  }

  const checkStatusArrow = function (status) {
    if (status === 1) {
      addCurrentColor(currentColor)
      tooth.baseColor = currentColor
      tooth.arrowColor[1] = currentColor
      tooth.arrowColor[0] = defaultColor
    } else if (status === 2) {
      const tempColor = tooth.arrowColor[1]
      tooth.baseColor = currentColor
      tooth.arrowColor[0] = currentColor
      tooth.arrowColor[1] = defaultColor
      checkMatchColor(tempColor)
      includesCurrentColor(tempColor)
    } else if (status === 3) {
      tooth.baseColor = tooth.arrowColor[0] = tooth.arrowColor[1] = currentColor
      checkMatchColor(currentColor)
      includesCurrentColor(currentColor)
    } else {
      const tempColor = tooth.arrowColor[0]
      tooth.arrowColor[0] = tooth.arrowColor[1] = tooth.baseColor = defaultColor
      includesCurrentColor(tempColor)
    }
  }
  updateStatusArrow(tooth.arrowStatus)
  checkStatusArrow(tooth.arrowStatus)
}

function checkStatusTooth(tooth) {
  if (tooth.statusTooth === 'Normal') {
    return tooth.img?.svg
  } else if (tooth.statusTooth === 'Implant') {
    return tooth.img?.implantSvg
  } else {
    return tooth.img?.deleteSvg
  }
}

watch(isChangeFirm, () => {
  teethData.forEach((tooth) => {
    tooth.arrowColor[0] = tooth.arrowColor[1] = tooth.baseColor = defaultColor
    tooth.arrowStatus = 0
  })
  emit('resetChangeFirmStatus')
})

watch(isNewReport, () => {
  teethData.forEach((tooth) => {
    tooth.statusTooth = 'Normal'
  })
})

watch(isUpdateReport, () => {
  getFullReport()
  emit('updateReport')
})

watch(teethData, (newValue) => {
  emit('updateTeethData', newValue)
})

watch(deletedColor, (newValue) => {
  const correctTeeth = teethData.filter(
    (tooth) =>
      tooth.arrowColor[0] === newValue || tooth.arrowColor[1] === newValue
  )
  correctTeeth.forEach((tooth) => {
    tooth.arrowColor[0] = tooth.arrowColor[1] = tooth.baseColor = defaultColor
  })
  emit('resetDeletedColor', 'reset')
})

watch(reportId, () => {
  if (route.name === 'BrushMapPage') {
    if (reportId.value) {
      getFullReport()
    }
  }
})

onMounted(() => {
  if (reportId.value) {
    getFullReport()
  }
  emit('updateTeethData', teethData)
})
</script>

<template>
  <div
    id="teeth"
    :class="{
      'is-preview': isPreviewMode,
      'brush-map__teeth_preview': isPreviewMode,
      'is-normal': !isPreviewMode,
    }"
    class="brush-map__teeth"
  >
    <img v-if="isPreviewMode" :src="qrCode" alt="" class="brush-map__qr" />
    <div v-for="tooth in teethData" :key="tooth.id" class="brush-map__tooth">
      <svg
        :class="tooth.class"
        :height="tooth.img?.height"
        :width="tooth.img?.width"
        class="brush-map__image"
        @click="updateStatusTooth(tooth)"
        v-html="checkStatusTooth(tooth)"
      />
      <span :class="tooth.numberClass" class="number-tooth">
        {{ tooth.id }}
      </span>
      <div
        v-if="tooth.arrowClass && !isPreviewMode"
        :class="tooth.arrowClass"
        class="arrow brush-map__image"
        draggable="false"
      >
        <svg
          fill="none"
          height="59"
          viewBox="0 0 16 59"
          width="16"
          xmlns="http://www.w3.org/2000/svg"
          @click="updateArrayColor(tooth)"
        >
          <path
            v-if="tooth.arrowColor[0] !== '#b3b3b3'"
            :stroke="tooth.arrowColor[0]"
            d="M13.9261 46.4146L7.96482 33.6518L2.03702 45.6803"
            stroke-linecap="round"
            stroke-width="3.41646"
          />
          <path
            :stroke="tooth.baseColor"
            d="M7.94531 57.5918L7.94531 1.5918"
            stroke-dasharray="0.85 2.56"
            stroke-linecap="round"
            stroke-width="1.70823"
          />
          <path
            v-if="tooth.arrowColor[1] !== '#b3b3b3'"
            :stroke="tooth.arrowColor[1]"
            d="M1.99971 13.2001L7.96096 25.963L13.8888 13.9345"
            stroke-linecap="round"
            stroke-width="3.41646"
          />
          <circle
            :stroke="tooth.baseColor"
            cx="8"
            cy="30"
            fill="white"
            r="4.14589"
            stroke-width="1.70823"
          />
        </svg>
      </div>
      <!-- arrow -->
      <div
        v-if="tooth.arrowClass && isPreviewMode"
        :class="tooth.arrowClass"
        class="arrow brush-map__image"
        draggable="false"
      >
        <svg
          class="arrow__image"
          fill="none"
          height="23"
          viewBox="0 0 58 23"
          width="58"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            v-if="tooth.arrowColor[0] !== '#b3b3b3'"
            :stroke="tooth.arrowColor[0]"
            d="M47.6165 8.96911L33.8405 11.9098L44.2292 20.389"
            stroke-linecap="round"
            stroke-width="3.41646"
          />
          <path
            :stroke="tooth.baseColor"
            d="M1.10352 5.93311L56.621 16.1825"
            stroke-dasharray="0.85 2.56"
            stroke-linecap="round"
            stroke-width="1.70823"
          />
          <path
            v-if="tooth.arrowColor[1] !== '#b3b3b3'"
            :stroke="tooth.arrowColor[1]"
            d="M12.209 14.0471L25.8748 10.6306L15.1984 2.5166"
            stroke-linecap="round"
            stroke-width="3.41646"
          />
        </svg>
        <svg
          class="arrow__circle"
          draggable="false"
          fill="none"
          height="11"
          viewBox="0 0 10 11"
          width="10"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            :stroke="tooth.baseColor"
            cx="5.08435"
            cy="5.49548"
            fill="white"
            r="3.84352"
            stroke-width="1.70823"
          />
        </svg>
      </div>
      <!-- arrow -->
    </div>
    <!-- brush-map__tooth -->
    <svg
      :class="{ 'brush-map__symbol-left_preview': isPreviewMode }"
      class="brush-map__symbol-left"
      fill="none"
      height="19"
      viewBox="0 0 12 19"
      width="12"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11.5895 16.135V18.5962H0.222451V0.289678H3.06421V16.135H11.5895Z"
        fill="#B3B3B3"
      />
    </svg>

    <svg
      :class="{ 'brush-map__symbol-right_preview': isPreviewMode }"
      class="brush-map__symbol-right"
      fill="none"
      height="19"
      viewBox="0 0 15 19"
      width="15"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.21265 2.67473V9.25898H7.19619C9.39094 9.25898 10.6469 8.05377
        10.6469 5.96051C10.6469 3.918 9.31482 2.67473 7.12007
        2.67473H3.21265ZM3.21265 11.5552V18.5962H0.370889V0.289678H7.4626C11.2305
        0.289678 13.5775 2.47174 13.5775 5.88439C13.5775 8.35825 12.2581 10.3627
        10.0253 11.1112L14.123 18.5962H10.8499L7.12007 11.5552H3.21265Z"
        fill="#B3B3B3"
      />
    </svg>
  </div>
  <!-- brush-map__teeth -->
</template>

<style lang="scss" scoped>
@import '@/apps/styles/resources/index';
@import '@/apps/styles/teeth';

.is-preview {
  .brush-map__image {
    pointer-events: none;
  }
}

.arrow {
  position: absolute;
  z-index: 3;
  cursor: pointer;
  pointer-events: visibleFill;
  user-select: none;

  &__image {
    position: absolute;
    left: 0;
    top: 0;
  }

  &__circle {
    position: absolute;
    left: 24.2px;
    top: 5.8px;
    transition: 0.3s;
  }
}

.number-tooth {
  position: absolute;
  font-family: 'SF Pro Display', sans-serif;
  font-weight: 400;
  font-size: $size--12;
  line-height: 14px;
  color: #b3b3b3;
}

.brush-map {
  &__teeth {
    position: relative;
    margin-top: 0;
    margin-left: 60px;
    width: 316px;
    transform: scaleX(1.1);

    &_preview {
      margin-top: 85px;
    }
  }

  &__qr {
    position: absolute;
    right: -125px;
    top: -50px;
    width: 70px;
    height: 70px;

    @media screen and (width < 1450px) {
      right: -113px;
    }
  }

  &__tooth {
    width: fit-content;
  }

  &__image {
    position: absolute;
    cursor: pointer;
    pointer-events: visibleFill;
    user-select: none;
  }

  &__symbol {
    &-left {
      position: absolute;
      left: -8px;
      top: 341px;

      &_preview {
        left: 9px;
        top: 256px;
      }
    }

    &-right {
      position: absolute;
      right: -16.8px;
      top: 340px;

      &_preview {
        right: 4px;
        top: 256px;
      }
    }
  }
}
</style>
